import { useCallback } from 'react'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { CaretUpDown as CaretUpDownIcon } from '@phosphor-icons/react/dist/ssr/CaretUpDown'
import noop from 'lodash/noop'
import { useNavigate } from 'react-router-dom'

import { usePopover } from '@/hooks/use-popover'
import { useWorkspaces } from '@/hooks/use-workspaces'

import { UserAvatar } from '@/components/core/user-avatar'
import { WorkspacesPopover } from './workspaces-popover'

export function WorkspacesSwitch({ mini = false }) {
	const navigate = useNavigate()
	const popover = usePopover<HTMLDivElement>()
	const { currentOperator, canSwitchOperator, isLoading, updateOperator } = useWorkspaces()

	const handleOperatorChange = useCallback(
		(operatorId: number) => {
			updateOperator(operatorId)
			popover.handleClose()
			navigate(0)
		},
		[popover, navigate, updateOperator]
	)

	return (
		<>
			<Stack
				direction="row"
				onClick={canSwitchOperator ? popover.handleOpen : noop}
				ref={popover.anchorRef}
				spacing={2}
				sx={{
					alignItems: 'center',
					border: `1px solid ${mini ? 'transparent' : 'var(--Workspaces-border-color)'}`,
					borderRadius: '12px',
					cursor: canSwitchOperator ? 'pointer' : 'default',
					height: '55.98px',
					p: mini ? '4px 0px' : '4px 8px',
					ml: mini ? '-2px' : '0px',
					minWidth: 0,
					width: '100%',
				}}
			>
				{isLoading ? (
					<Skeleton variant="rounded">
						<Avatar variant="rounded" />
					</Skeleton>
				) : (
					<UserAvatar
						src={currentOperator?.photo}
						name={currentOperator?.name}
						alt={currentOperator?.name}
						variant="rounded"
						sx={{
							flexShrink: 0,
							bgcolor: currentOperator?.photo ? 'common.white' : 'none',
							'& .MuiAvatar-img': { objectFit: 'contain' },
						}}
					/>
				)}

				{!mini && (
					<>
						<Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
							{isLoading ? (
								<Skeleton variant="rounded" height={40} />
							) : (
								<>
									<Typography color="var(--Workspaces-title-color)" variant="caption">
										Operator
									</Typography>
									<Typography color="var(--Workspaces-name-color)" variant="subtitle2" noWrap>
										{currentOperator?.name}
									</Typography>
								</>
							)}
						</Box>
						{canSwitchOperator ? (
							<Box sx={{ flexShrink: 0, alignItems: 'center', display: 'flex' }}>
								<CaretUpDownIcon color="var(--Workspaces-expand-color)" fontSize="var(--icon-fontSize-sm)" />
							</Box>
						) : null}
					</>
				)}
			</Stack>
			<WorkspacesPopover
				selectedOperatorId={currentOperator?.id}
				anchorEl={popover.anchorRef.current}
				onChange={handleOperatorChange}
				onClose={popover.handleClose}
				open={!isLoading && popover.open}
			/>
		</>
	)
}
